import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import { resolveIcon } from "./resolve-icon"

export function ButtonRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext, mode } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("button")
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  // Check if we have children (from rehype pipeline: button → span → text)
  const hasChildren = !text && element.children && element.children.length > 0

  // Resolve dynamic icon names: #{state.isPlaying ? 'pause' : 'play'} → "pause"
  // Accept `name` as a fallback — many commerce-admin blocks were generated with
  // `"name": "chevron-left"` instead of the canonical `"icon"` field. Treat it as
  // the icon slot for button elements; real HTML form name attributes are not used
  // on buttons in Cogo blocks (verified across all deployed apps).
  const rawIcon = element.icon ?? (element as { name?: string }).name ?? ""
  const iconName = rawIcon.includes("#{")
    ? String(resolveSymbol(rawIcon) ?? "")
    : rawIcon
  const IconComponent = iconName ? resolveIcon(iconName) : undefined

  // Valid variant/size sets — Normalizer guarantees these values for normalized blocks.
  // Non-normalized blocks get "default" fallback.
  type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "none"
  type ButtonSize = "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg"
  const VALID_VARIANTS: ReadonlySet<string> = new Set(["default", "destructive", "outline", "secondary", "ghost", "link", "none"])
  const VALID_SIZES: ReadonlySet<string> = new Set(["default", "xs", "sm", "lg", "icon", "icon-xs", "icon-sm", "icon-lg"])

  // Resolve dynamic variant expressions like #{ui.selectedDateRange === '1month' ? 'default' : 'outline'}
  const rawVariant = element.variant
    ? (element.variant.includes("#{") ? String(resolveSymbol(element.variant) ?? "") : element.variant)
    : (atomDefaults.defaultVariant ?? "default")
  const variant: ButtonVariant = VALID_VARIANTS.has(rawVariant) ? (rawVariant as ButtonVariant) : "default"
  const rawSize = element.size ?? atomDefaults.defaultSize ?? "default"
  const size: ButtonSize = VALID_SIZES.has(rawSize) ? (rawSize as ButtonSize) : "default"

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (mode !== "edit") e.stopPropagation()
    // Always dispatch tap — both inline events and external bindings (events.cogo.json) are handled
    // by the event dispatcher. itemContext enables iteration variable resolution (e.g., #{product.id})
    dispatchEvent(element.id, "tap", itemContext)
  }

  // Resolve disabled/loading state from @{...} or #{...} expressions
  const rawDisabled = element.disabled
  const isDisabled = typeof rawDisabled === "string"
    ? !!resolveSymbol(rawDisabled)
    : !!rawDisabled
  const rawLoading = element.loading
  const isLoadingBtn = typeof rawLoading === "string"
    ? !!resolveSymbol(rawLoading)
    : !!rawLoading

  const extraClasses = cn(
    element.fill && "w-full",
    stripPresetConflicts(element.className),
  )

  const iconEl = IconComponent ? <IconComponent className={element.iconClassName ?? "h-4 w-4"} /> : null
  const iconRight = element.iconPosition === "right"

  // Render children as content when no direct text (from rehype pipeline)
  const childContent = hasChildren
    ? element.children!.map((child, idx) => (
        <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
      ))
    : null

  // Link button: wrap in <a> if href is provided
  if (element.href) {
    const href = String(resolveSymbol(String(element.href)) ?? "")
    const target = element.target as string | undefined
    const rel = element.rel as string | undefined ?? (target === "_blank" ? "noopener noreferrer" : undefined)
    return (
      <a href={href} target={target} rel={rel}>
        <Button
          variant={variant}
          size={size}
          className={extraClasses || undefined}
          disabled={isDisabled || isLoadingBtn}
          data-action="navigate"
          data-variant={variant}
          data-size={size}
        >
          {isLoadingBtn && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
          {!iconRight && !isLoadingBtn && iconEl}
          {childContent ?? text}
          {iconRight && iconEl}
        </Button>
      </a>
    )
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={extraClasses || undefined}
      onClick={handleClick}
      disabled={isDisabled || isLoadingBtn}
      data-action="action"
      data-variant={variant}
      data-size={size}
    >
      {isLoadingBtn && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {!iconRight && !isLoadingBtn && iconEl}
      {childContent ?? text}
      {iconRight && iconEl}
    </Button>
  )
}
